import { AlignedRow, EntityData } from '../types'

interface MRDData {
  rows?: EntityData[]
  [key: string]: unknown
}

interface MappingData extends EntityData {
  goids?: string[]
  _id?: string
  id?: string
}

/**
 * Align rows across MRD data and tenant mappings
 */
export const alignRowsAcrossGrids = (
  mrdData: MRDData,
  leftMappings: MappingData[] = [],
  rightMappings: MappingData[] = [],
): AlignedRow[] => {
  // Handle both array format and object with rows property
  const dataArray = Array.isArray(mrdData) ? mrdData : mrdData?.rows || []
  if (!dataArray || dataArray.length === 0) return []

  const mappedRows: AlignedRow[] = []
  const unmappedRows: AlignedRow[] = []
  const mrdRows = [...dataArray]
  const leftMaps = leftMappings || []
  const rightMaps = rightMappings || []

  // Create a map of GOIDs to mappings for quick lookup
  const leftGoidMap = new Map<string, MappingData>()
  const rightGoidMap = new Map<string, MappingData>()
  const usedLeftIds = new Set<string>()
  const usedRightIds = new Set<string>()

  leftMaps.forEach((mapping: MappingData) => {
    if (mapping.goids && mapping.goids.length > 0) {
      mapping.goids.forEach((goid: string) => {
        leftGoidMap.set(goid, mapping)
      })
    }
  })

  rightMaps.forEach((mapping: MappingData) => {
    if (mapping.goids && mapping.goids.length > 0) {
      mapping.goids.forEach((goid: string) => {
        rightGoidMap.set(goid, mapping)
      })
    }
  })

  // First pass: Map MRD rows to tenant rows when at least one tenant has a mapping
  mrdRows.forEach((mrdRow: EntityData) => {
    const leftMapping = leftGoidMap.get(mrdRow.goid)
    const rightMapping = rightGoidMap.get(mrdRow.goid)
    const hasAnyMapping = !!(leftMapping || rightMapping)

    // Skip MRD rows that have no mappings on either side to mirror the original experience
    if (!hasAnyMapping) {
      return
    }

    if (leftMapping) usedLeftIds.add(leftMapping._id || leftMapping.id)
    if (rightMapping) usedRightIds.add(rightMapping._id || rightMapping.id)

    const alignedRow: AlignedRow = {
      id: `mrd-${mrdRow.goid}`,
      mrdData: mrdRow,
      leftData: leftMapping || null, // null if no left mapping
      rightData: rightMapping || null, // null if no right mapping
      isMapped: hasAnyMapping, // true only if has at least one mapping
      mappingId: leftMapping?._id || rightMapping?._id,
    }

    // At least one tenant is mapped to this MRD record
    mappedRows.push(alignedRow)
  })

  // Second pass: Add unmapped left tenant entities
  leftMaps.forEach((mapping: MappingData) => {
    const mappingId = mapping._id || mapping.id
    if (
      !usedLeftIds.has(mappingId) &&
      (!mapping.goids || mapping.goids.length === 0)
    ) {
      unmappedRows.push({
        id: `left-${mappingId}`,
        leftData: mapping,
        mrdData: null,
        rightData: null,
        isMapped: false,
      })
    }
  })

  // Third pass: Add unmapped right tenant entities
  rightMaps.forEach((mapping: MappingData) => {
    const mappingId = mapping._id || mapping.id
    if (
      !usedRightIds.has(mappingId) &&
      (!mapping.goids || mapping.goids.length === 0)
    ) {
      unmappedRows.push({
        id: `right-${mappingId}`,
        leftData: null,
        mrdData: null,
        rightData: mapping,
        isMapped: false,
      })
    }
  })

  // Sort mapped rows by MRD name
  mappedRows.sort((a, b) => {
    const nameA = (a.mrdData?.name || '').toLowerCase()
    const nameB = (b.mrdData?.name || '').toLowerCase()
    return nameA.localeCompare(nameB)
  })

  // Sort unmapped rows by name
  unmappedRows.sort((a, b) => {
    const nameA = (
      a.leftData?.display_name ||
      a.leftData?.name ||
      a.rightData?.display_name ||
      a.rightData?.name ||
      ''
    ).toLowerCase()
    const nameB = (
      b.leftData?.display_name ||
      b.leftData?.name ||
      b.rightData?.display_name ||
      b.rightData?.name ||
      ''
    ).toLowerCase()
    return nameA.localeCompare(nameB)
  })

  // Combine: mapped rows first (sorted by name), then unmapped rows (sorted by name)
  return [...mappedRows, ...unmappedRows]
}

/**
 * Filter entities that don't already have mappings on the same side
 */
export const filterAvailableEntities = (
  side: 'left' | 'right',
  alignedRows: AlignedRow[] = [],
  mrdEntities: EntityData[] = [],
): EntityData[] => {
  if (!Array.isArray(alignedRows) || alignedRows.length === 0) {
    return Array.isArray(mrdEntities) ? [...mrdEntities] : []
  }
  const sourceEntities = Array.isArray(mrdEntities) ? mrdEntities : []
  // Get GOIDs that already have mappings on the SAME side
  const mappedGoids = new Set<string>()
  alignedRows.forEach((row) => {
    if (row.mrdData?.goid) {
      // If we're on the left side, exclude GOIDs that already have left mappings
      // If we're on the right side, exclude GOIDs that already have right mappings
      const hasSameSideMapping =
        side === 'left' ? !!row.leftData : !!row.rightData
      if (hasSameSideMapping) {
        mappedGoids.add(row.mrdData.goid)
      }
    }
  })

  return sourceEntities.filter((entity) => !mappedGoids.has(entity.goid))
}
